import { useEffect, useRef } from "react";
import * as signalR from "@microsoft/signalr";
import type { NotificationItem, TaskReminderItem } from "@/types/domain";

// Same-origin negotiate proxy: the server adds the hub token, so it never reaches the browser.
const SIGNALR_URL = "/api/signalr";

type Options = {
  onNotification: (n: NotificationItem) => void;
  onTask: (t: TaskReminderItem) => void;
};

export function useSignalRNotifications({ onNotification, onTask }: Options) {
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const onNotificationRef = useRef(onNotification);
  const onTaskRef = useRef(onTask);
  onNotificationRef.current = onNotification;
  onTaskRef.current = onTask;

  useEffect(() => {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(SIGNALR_URL)
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connectionRef.current = connection;

    connection.on("ReceiveNotification", (json: string) => {
      try {
        const n: NotificationItem = JSON.parse(json);
        onNotificationRef.current(n);
      } catch {/* non-critical */}
    });

    connection.on("ReceiveTaskNotification", (json: string) => {
      try {
        const t: TaskReminderItem = JSON.parse(json);
        onTaskRef.current(t);
      } catch {/* non-critical */}
    });

    connection.on("SessionClosed", (reason: string) => {
      window.location.href = `/ErrorPages/SessionClosed.aspx?status=${reason}`;
    });

    async function start() {
      try {
        await connection.start();
      } catch {
        setTimeout(start, 5000);
      }
    }

    connection.onclose(async () => { await start(); });
    start();

    return () => {
      connection.stop();
    };
  }, []);
}
